import * as d3 from "d3"
import { IntlShape } from "gatsby-plugin-intl"
import { AppState } from "../../data/redux-state"
import { extractHumanReadableSubVar } from "../../utils/extractHumanReadableSubVar"
import { applyDerivToSubVar } from "../../utils/derivParam"
import {
  applyCumulToSubVar,
  extractUnitFromVariableName,
} from "../../utils/cumulParam"
import {
  getLineType,
  getMaxAbsDataValue,
  UncertaintyLine,
} from "../../utils/extractLines"
import {
  ExplicitSymbolType,
  resolveEwmSymbolType,
} from "../../utils/generateLine"
import { getTranslatedGeoVariableName } from "../VariableDropdown"
import { getCumulSetUnitParams } from "../../utils/unitParam"
import {
  formatToSignificantDigits,
  getFlyoutSignificantDigits,
} from "../../utils/format"
import { translateGeoRegion } from "../../utils/translateGeoRegion"
import {
  extractCoVarOptions,
  filterCoVarsWithAvailableData,
  formatSelectedCoVarsDescription,
  orderSelectedCoVarsByDropdownOrder,
} from "../../utils/extractCoVariables"
import {
  appendWrappedLegendText,
  getLegendRegionColumnWidths,
  LEGEND_REGION_COLUMN_GAP,
  LEGEND_REGION_TEXT_MARKER_GAP,
  LEGEND_ROW_HEIGHT_MULTIPLIER,
  splitLegendRegionEntriesIntoRows,
} from "./legendLayout"

export const LEGEND_TOP_OFFSET = 7

// Class marking the legend elements inside the chart SVG. The PNG export
// queries it to compute crop bounds.
export const SVG_LEGEND_CLASS = "chart-svg-legend"

// Draws a legend marker matching the chart's compVar symbol type. The diamond
// and square are sized to read at a similar visual weight as the circle (a
// diamond inscribed in the circle's bounding radius has area 2r² vs πr², so
// it needs enlargement).
function appendLegendMarker(
  group: d3.Selection<SVGGElement, unknown, any, any>,
  cx: number,
  cy: number,
  radius: number,
  color: string,
  symbolType: ExplicitSymbolType
) {
  if (symbolType === "diamond") {
    const r = radius * 1.23
    group
      .append("polygon")
      .attr(
        "points",
        `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`
      )
      .attr("fill", color)
  } else if (symbolType === "square") {
    const side = radius * 1.7
    group
      .append("rect")
      .attr("x", cx - side / 2)
      .attr("y", cy - side / 2)
      .attr("width", side)
      .attr("height", side)
      .attr("fill", color)
  } else {
    group
      .append("circle")
      .attr("cx", cx)
      .attr("cy", cy)
      .attr("r", radius)
      .attr("fill", color)
  }
}

export function drawLegendOverlay(args: {
  svg: d3.Selection<SVGGElement, unknown, any, any>
  centeredOverlayX: number
  centeredOverlayWidth: number
  svgWidth: number
  data: UncertaintyLine[]
  appState: AppState
  intl: IntlShape
}) {
  const legendSvgX = args.centeredOverlayX
  const legendSvgY = LEGEND_TOP_OFFSET
  const legendSvgW = args.centeredOverlayWidth

  // Render legend text immediately after background rect, before data,
  // so data is painted on top of the legend text.
  const legendFontSize = args.svgWidth < 1000 ? 8 : 12
  const circleRadius = args.svgWidth < 1000 ? 3 : 5
  const rowHeight = legendFontSize * LEGEND_ROW_HEIGHT_MULTIPLIER
  const charWidth = legendFontSize * 0.6
  const legendLeftX = legendSvgX + 5
  const legendRightX = legendSvgX + legendSvgW - 5
  const legendTextGroup = args.svg.append("g")
    .attr("class", SVG_LEGEND_CLASS)
    .attr("font-family", "HKGrotesk_regular")
    .attr("font-size", legendFontSize + "px")
    .attr("fill", "black")
  const getLegendTextWidth = (text: string) => {
    const measurement = legendTextGroup.append("text")
      .attr("x", -9999)
      .attr("y", -9999)
      .attr("visibility", "hidden")
      .text(text)
    const width = measurement.node()?.getComputedTextLength() ?? text.length * charWidth
    measurement.remove()
    return width
  }
  // cumulSetUnit replaces the VarUnit in the legend's `nnnnnVarUnit` part; the
  // anchor value arrives pre-scaled from the extractLines pipeline and is shown
  // with the same precision as the flyout menu of clicked data values.
  const cumulUnitOverride = getCumulSetUnitParams()
  const flyoutSignificantDigits = getFlyoutSignificantDigits(
    getMaxAbsDataValue(args.data)
  )
  const variableUnit =
    cumulUnitOverride?.unitString ??
    extractUnitFromVariableName(
      getTranslatedGeoVariableName(
        args.appState.indicatorClass,
        args.appState.geoVariable!,
        args.intl
      )
    )
  let currentY = legendSvgY + rowHeight
  args.appState.subVariable.forEach((subVarKey: string) => {
    // Prefer the observation/primary line's reference-year anchor for the legend.
    const anchorLine =
      args.data.find(
        (d) =>
          d.subVarKey === subVarKey &&
          getLineType(d) === "OBSERVATION" &&
          d.cumulRefValue !== undefined
      ) ??
      args.data.find(
        (d) => d.subVarKey === subVarKey && d.cumulRefValue !== undefined
      )
    const subVar = applyCumulToSubVar(
      applyDerivToSubVar(
        extractHumanReadableSubVar(args.appState.sourceJson, subVarKey, args.intl)
      ),
      anchorLine?.cumulRefValue === undefined
        ? undefined
        : formatToSignificantDigits(
            anchorLine.cumulRefValue,
            flyoutSignificantDigits
          ),
      variableUnit
    )
    const regionEntries = [...args.appState.geoRegion].reverse().map((geoRegionKey: string) => {
      const linesForRegion = args.data.filter(
        (d) => d.subVarKey === subVarKey && d.geoRegionKey === geoRegionKey
      )
      // Prefer the SubVariable's primary compVar line for both color and symbol
      // type, so the legend marker matches the primary timeseries plotting.
      const matchingLine =
        linesForRegion.find((d) => getLineType(d) === "OBSERVATION") ??
        linesForRegion[0]
      const symbolType: ExplicitSymbolType =
        resolveEwmSymbolType(
          args.appState.indicatorClass,
          matchingLine?.coVar.Metadata.SymbolType
        ) ?? "circle"
      return {
        color: matchingLine ? matchingLine.color : "#000",
        text: translateGeoRegion(geoRegionKey, args.intl),
        symbolType,
      }
    })
    const legendWidth = legendRightX - legendLeftX
    const minSubVarWidth = Math.max(charWidth * 18, legendWidth * 0.4)
    const maxRegionRowWidth = Math.max(charWidth * 18, legendWidth - minSubVarWidth)
    const regionRows = splitLegendRegionEntriesIntoRows(
      regionEntries,
      maxRegionRowWidth,
      3,
      circleRadius,
      getLegendTextWidth
    )
    const regionColumnWidths = getLegendRegionColumnWidths(
      regionRows,
      circleRadius,
      getLegendTextWidth
    )
    const regionBlockWidth = regionColumnWidths.reduce((width: number, columnWidth) => {
      return width + columnWidth
    }, 0) + Math.max(regionColumnWidths.length - 1, 0) * LEGEND_REGION_COLUMN_GAP
    const subVarWidth = Math.max(minSubVarWidth, legendWidth - regionBlockWidth)
    const subVarLineCount = appendWrappedLegendText(
      legendTextGroup,
      legendLeftX,
      currentY,
      subVar,
      subVarWidth,
      rowHeight
    )

    regionRows.forEach((row, rowIndex) => {
      const rowY = currentY + rowIndex * rowHeight
      const firstColumnIndex = regionColumnWidths.length - row.length
      const columnRightEdges = [...regionColumnWidths]
      let runningRightEdge = legendRightX

      for (let columnIndex = regionColumnWidths.length - 1; columnIndex >= 0; columnIndex -= 1) {
        columnRightEdges[columnIndex] = runningRightEdge
        runningRightEdge -= regionColumnWidths[columnIndex] + LEGEND_REGION_COLUMN_GAP
      }

      row.forEach((entry, entryIndex) => {
        const columnIndex = firstColumnIndex + entryIndex
        const markerCenterX = columnRightEdges[columnIndex] - circleRadius
        const textX = markerCenterX - circleRadius - LEGEND_REGION_TEXT_MARKER_GAP
        legendTextGroup.append("text")
          .attr("x", textX)
          .attr("y", rowY)
          .attr("text-anchor", "end")
          .text(entry.text)
        appendLegendMarker(
          legendTextGroup,
          markerCenterX,
          rowY - circleRadius,
          circleRadius,
          entry.color,
          entry.symbolType
        )
      })
    })
    currentY += rowHeight * Math.max(subVarLineCount, regionRows.length, 1)
  })
  // CoVars description; grayed-out (dataless) selections are excluded so the
  // legend string matches the droplist graying.
  const availableCoVars = extractCoVarOptions(args.appState, args.intl)
  const coVars = orderSelectedCoVarsByDropdownOrder({
    appState: args.appState,
    selectedCoVars: filterCoVarsWithAvailableData({
      selectedCoVars: args.appState.coVarOptions,
      availableCoVars,
    }),
    availableCoVars,
    intl: args.intl,
  })
  if (coVars && coVars.length > 0) {
    const coVarsDescription = formatSelectedCoVarsDescription({
      appState: args.appState,
      coVars,
      intl: args.intl,
    })
    if (coVarsDescription) {
      currentY += rowHeight * (appendWrappedLegendText(
        legendTextGroup,
        legendLeftX,
        currentY,
        coVarsDescription,
        legendRightX - legendLeftX,
        rowHeight
      ) - 1)
    }
  }

  const legendSvgH = currentY - legendSvgY + rowHeight * 0.5
  args.svg
    .append("rect")
    .attr("class", SVG_LEGEND_CLASS)
    .attr("x", legendSvgX)
    .attr("y", legendSvgY)
    .attr("width", legendSvgW)
    .attr("height", legendSvgH)
    .attr("fill", "#efefefa6")
  legendTextGroup.raise()
}